/**
 * Widget registry — maps a widget-type id to:
 *   - the React component that renders it
 *   - the default size in grid units
 *   - the display name + icon + category for the (B0.9) palette
 *
 * The renderer reads dashboard JSON, looks up each widget instance's `type`
 * here, and calls the registered React component with config + datasource
 * resolved to props.
 */

import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Activity,
  Bell,
  Boxes,
  Grid3x3,
  Image as ImageIcon,
  Inbox,
  LineChart,
  Sparkles,
  Table as TableIcon,
} from "lucide-react";
import { ChartSkeleton, type SkeletonShape } from "@/lib/widgets/ChartSkeleton";
import { EquipmentOverviewCard } from "@/lib/widgets/EquipmentOverviewCard";
import {
  AlarmList,
  EventFeed,
  GaugeChart,
  HistogramChart,
  KPICard,
  ParetoChart,
  Table,
  TimeseriesChart,
  type ColumnDef,
  type TimeseriesSeries,
} from "@/lib/widgets";
import { DefectParetoChart } from "@foundry/DefectParetoChart";
import { CorrelationHeatmap } from "@foundry/CorrelationHeatmap";
import { OEEPanel, syntheticOEEHistory } from "@foundry/OEEPanel";
import {
  FX_BATH_TEMP,
  FX_POWER_KW,
  FX_SAND_MOISTURE,
  FX_ALARMS,
  FX_EVENTS,
  FX_KPI_SPARK,
  FX_DEVICES,
} from "@/lib/widgets/__fixtures";
import {
  FX_DEFECT_PARETO,
  FX_CORRELATION_CELLS,
  FX_CORRELATION_LABELS,
  FX_CORRELATION_PARAMS,
  FX_BATCHES,
} from "@/lib/widgets/__foundry_fixtures";
// Re-use FX_BATH_TEMP / FX_SAND_MOISTURE for SPC sample sources.
import { readSlot, type DataSource, type WidgetSlot, type WidgetType } from "./types";
import { ScatterPlot } from "@foundry/ScatterPlot";
import { ImageDatapointPanel } from "@/components/images/ImageDatapointPanel";
import type { ImportedRow } from "@/lib/imports/types";
import {
  aggregateDefectPareto,
  aggregateTopProblemParts,
} from "@/lib/imports/aggregations";
import { windowLabel, type LiveSpan, type LiveTagData, type RangeToken, type WidgetWindow } from "./use-live-tag-data";
import { lotsAnswer, scrapTrendOf, useLotAggregates } from "./lot-aggregates";
import { pairSeriesByTime } from "./scatter-pairing";
import { statusCells, type StatusCell } from "./status-grid";
import { lastObservations, observedValues } from "./series-values";
import { SpcTagChart } from "./spc-widget";
import { bandText, seriesBand, withSentence, type WidgetBand } from "./port-binding";
import { useTwinAggregates, resolveTwinKpi, type TwinAggregates } from "./twin-aggregates";
import { useOptionalAlarms } from "@/lib/alarms/store";
import { isLiveApi, listOrgTags, useTwinCorrelationsQuery } from "@/lib/api";
import { plantTimeApi } from "@/lib/shift-calendar";
import { chartReadingNames, noReadingsWords, noScrapWords, plantClockText, readingName } from "./reading-names";

/** What a widget's `loading` is given. `keys` is how many tags it reads. */
interface LoadingProps {
  title?: string;
  subtitle?: string;
  config?: Record<string, unknown>;
  keys: number;
}

export interface WidgetRegistryEntry {
  type: WidgetType;
  name: string;
  description: string;
  icon: typeof Activity;
  category: "kpi" | "timeseries" | "categorical" | "statistical" | "event" | "table" | "spatial" | "industrial" | "content";
  defaultSize: { w: number; h: number };
  /**
   * Slot contract — what kinds of data this widget can be bound to, and
   * what role each binding plays. The dashboard editor reads this to
   * drive the per-slot picker UI and to block invalid bindings before
   * they render. See `lib/dashboard/data-kinds.ts` for the DataKind enum.
   */
  accepts: WidgetSlot[];
  /**
   * Render function — receives the widget instance's config + dataSource
   * and returns a React node. Phase 0 ignores dataSource and renders
   * against fixtures; B0.9 onward wires this to /v1/iot/tags/:id/values
   * and /v1/twin/entities/:id/port-values.
   *
   * B2.3a-bis: when `dataSource.importedDatasetId` is set, the
   * DashboardRenderer pre-resolves the matching rows via
   * `useImportedRows().byDataset(id)` and passes them here. Widgets
   * that support imported data switch to the aggregation helpers
   * (lib/imports/aggregations) when this prop is present; others
   * fall through to fixture-driven rendering.
   */
  /**
   * What the widget draws while the tags it reads are on their way, so a reading that is coming never says "No data"
   * first (WidgetRenderer). Null for a widget that reads no tag: it waits for its own source itself, or has none to
   * wait for.
   */
  loading: ((props: LoadingProps) => ReactNode) | null;
  render: (props: {
    title?: string;
    subtitle?: string;
    config?: Record<string, unknown>;
    dataSource?: DataSource;
    importedRows?: ImportedRow[];
    /**
     * Live readings for the widget's tag bindings (latest + recent series),
     * keyed by lower-cased bound key. Present in live mode when the bound tag
     * resolves; resolvers prefer it over imported/fixture data. See
     * `use-live-tag-data.ts`.
     */
    liveTagData?: LiveTagData;
    /**
     * The span `liveTagData` was read over, in epoch ms; `anchored` when a lookback counted back from the tags' last
     * reading. The time-series chart runs its time axis over it.
     */
    liveSpan?: LiveSpan;
    /**
     * The widget's resolved time window (the same one `liveTagData` was fetched
     * over). `undefined` → the wide default window. The table reads this to
     * choose latest-per-tag (undefined) vs per-bucket history (a pinned token);
     * chart widgets ignore it since their series is already windowed.
     */
    range?: RangeToken;
    /** A pinned window of days (`config.window`, the data's own period), which wins over `range`. */
    dataWindow?: WidgetWindow;
    /**
     * B6: the band each bound key draws, by its lower-cased key, in the unit its readings are shown in: the machine's
     * yardstick from its roles (`config.band.yardstick`), or the card's own ends (port-binding.ts widgetBands). A time
     * series draws it behind the key's line, a histogram as its two lines.
     */
    bands?: Record<string, WidgetBand>;
  }) => ReactNode;
}

/**
 * ImageWidget — renders the latest photo + history for a bound image datapoint.
 * The bound key is the tag NAME; org-tags resolves it to the host thing_id
 * (TagOption.device_id) so ImageDatapointPanel can list the photos. View-only on
 * a dashboard (capture happens on the equipment page / mobile app).
 */
function ImageWidget({
  title,
  subtitle,
  dataSource,
}: {
  title?: string;
  subtitle?: string;
  dataSource?: DataSource;
}) {
  const key = (readSlot(dataSource, "image")[0] ?? "").toLowerCase();
  const orgTags = useQuery({
    queryKey: ["org-tags"],
    queryFn: listOrgTags,
    enabled: isLiveApi && !!key,
    staleTime: 30_000,
  });
  const tag = (orgTags.data?.items ?? []).find(
    (t) => t.name?.toLowerCase() === key && t.data_kind === "image",
  );
  return (
    <div className="flex h-full flex-col gap-2 overflow-auto p-3">
      {(title || subtitle) && (
        <div className="flex flex-col">
          {title && <span className="text-sm font-medium text-on-surface">{title}</span>}
          {subtitle && <span className="text-xs text-on-surface-muted">{subtitle}</span>}
        </div>
      )}
      {tag ? (
        <ImageDatapointPanel thingId={tag.device_id} metric={tag.name} label={title ?? tag.name} />
      ) : (
        <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-border p-4 text-center text-xs text-on-surface-muted">
          {key
            ? "This datapoint isn't an image tag, or has no photos yet."
            : "Bind an image datapoint (add one on an equipment: Equipment → Manual-entry tags → Image)."}
        </div>
      )}
    </div>
  );
}

const STATUS_DOT: Record<StatusCell["state"], string> = {
  on: "bg-success",
  off: "border border-on-surface-muted",
  label: "bg-on-surface-muted",
  none: "border border-dashed border-on-surface-muted/60",
};

/**
 * The status grid on live data: each bound state tag's latest state (On or Off, or its label) and when it was read.
 * The empty state is for a grid with nothing bound, or none of whose tags has a reading.
 */
export function StatusGrid({ keys, liveTagData }: { keys: string[]; liveTagData?: LiveTagData }) {
  if (keys.length === 0) {
    return (
      <WidgetEmpty
        icon={Grid3x3}
        message="No status source"
        hint="Bind a state tag (online/offline, healthy/degraded) to populate this grid."
      />
    );
  }
  const cells = statusCells(keys, liveTagData);
  if (cells.every((c) => c.state === "none")) {
    return (
      <WidgetEmpty
        icon={Grid3x3}
        message="No readings yet"
        hint="The bound state tags have not reported a reading."
      />
    );
  }
  return (
    <ul className="grid h-full grid-cols-2 content-start gap-2 overflow-auto">
      {cells.map((c) => (
        <li
          key={c.key}
          className="flex min-w-0 flex-col gap-0.5 rounded-md border border-border bg-surface-elevated p-2 text-xs"
        >
          <span className="truncate font-medium text-on-surface" title={c.name}>
            {c.name}
          </span>
          <span className="flex min-w-0 items-center gap-1.5">
            <span className={`size-2 shrink-0 rounded-full ${STATUS_DOT[c.state]}`} aria-hidden />
            <span
              className={
                c.state === "none" ? "truncate text-on-surface-muted" : "truncate font-semibold text-on-surface"
              }
            >
              {c.text}
            </span>
          </span>
          {c.time && (
            <span className="text-[11px] text-on-surface-muted" title={new Date(c.time).toLocaleString()}>
              Updated {format(new Date(c.time), "d MMM, HH:mm")}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export const WIDGET_REGISTRY: Record<WidgetType, WidgetRegistryEntry> = {
  kpi: {
    type: "kpi",
    name: "KPI",
    description: "Single number with optional trend and delta.",
    icon: Activity,
    category: "kpi",
    defaultSize: { w: 3, h: 3 },
    accepts: [
      {
        id: "value",
        label: "Value",
        role: "value",
        required: true,
        multi: false,
        dataKinds: ["analog_scalar", "analog_percentage", "count_scalar", "derived_kpi"],
        hint: "Single numeric reading shown as the headline.",
      },
      {
        id: "trend",
        label: "Trend (optional)",
        role: "trend",
        required: false,
        multi: false,
        dataKinds: ["analog_scalar", "analog_percentage", "count_scalar"],
        hint: "Sparkline of the same metric over recent time.",
      },
    ],
    loading: ({ title, subtitle }) => (
      <KPICard label={title ?? "Metric"} hint={subtitle} data={{ value: 0, unit: "" }} state={{ kind: "loading" }} className="h-full" />
    ),
    render: ({ title, subtitle, dataSource, liveTagData, range, dataWindow }) => (
      <KpiWidget
        title={title}
        subtitle={subtitle}
        dataSource={dataSource}
        liveTagData={liveTagData}
        range={range}
        dataWindow={dataWindow}
      />
    ),
  },
  image: {
    type: "image",
    name: "Image",
    description: "Latest photo + history for an image datapoint.",
    icon: ImageIcon,
    category: "content",
    defaultSize: { w: 4, h: 5 },
    accepts: [
      {
        id: "image",
        label: "Image tag",
        role: "value",
        required: true,
        multi: false,
        dataKinds: ["image"],
        hint: "An image datapoint — shows its latest photo + history.",
      },
    ],
    loading: whileLoading("image"),
    render: ({ title, subtitle, dataSource }) => (
      <ImageWidget title={title} subtitle={subtitle} dataSource={dataSource} />
    ),
  },
  timeseries: {
    type: "timeseries",
    name: "Time-series",
    description: "Line / area chart of one or more tag streams over time.",
    icon: LineChart,
    category: "timeseries",
    defaultSize: { w: 6, h: 4 },
    accepts: [
      {
        id: "series",
        label: "Series",
        role: "series",
        required: true,
        multi: true,
        dataKinds: ["analog_scalar", "analog_percentage", "count_scalar"],
        hint: "One line per key. Mixed units auto-promote to dual Y-axis.",
      },
    ],
    loading: (props) => whileLoading(props.config?.variant === "area" ? "area" : "line")(props),
    render: ({ title, subtitle, dataSource, config, liveTagData, liveSpan, range, dataWindow, bands }) => (
      <TimeseriesWidget
        title={title}
        subtitle={subtitle}
        dataSource={dataSource}
        config={config}
        liveTagData={liveTagData}
        liveSpan={liveSpan}
        range={range}
        dataWindow={dataWindow}
        bands={bands}
      />
    ),
  },
  "alarm-list": {
    type: "alarm-list",
    name: "Alarm list",
    description: "Active + acknowledged + cleared alarms with ack action.",
    icon: Bell,
    category: "event",
    defaultSize: { w: 6, h: 5 },
    accepts: [
      {
        id: "events",
        label: "Alarm source",
        role: "events",
        required: false,
        multi: false,
        dataKinds: ["event_stream"],
        hint: "Optional filter. Unbound, this lists every alarm in the org from the alarm-rule engine.",
      },
    ],
    loading: null,
    render: ({ title, subtitle, config }) => {
      const limit = (config?.limit as number) ?? 5;
      return <AlarmListWidget title={title} subtitle={subtitle} limit={limit} />;
    },
  },
  "event-feed": {
    type: "event-feed",
    name: "Event feed",
    description: "Chronological log of recent process events.",
    icon: Inbox,
    category: "event",
    defaultSize: { w: 4, h: 5 },
    accepts: [
      {
        id: "events",
        label: "Event source",
        role: "events",
        required: false,
        multi: false,
        dataKinds: ["event_stream"],
        hint: "Optional filter. Unbound, this shows recent process events, batch transitions and alarm activity for the org.",
      },
    ],
    loading: null,
    render: ({ title, subtitle, config }) => {
      const limit = (config?.limit as number) ?? 5;
      return <EventFeedWidget title={title} subtitle={subtitle} limit={limit} />;
    },
  },
  "status-grid": {
    type: "status-grid",
    name: "Status grid",
    description: "At-a-glance state of multiple devices or entities.",
    icon: Grid3x3,
    category: "kpi",
    defaultSize: { w: 4, h: 3 },
    accepts: [
      {
        id: "state",
        label: "State",
        role: "state",
        required: false,
        multi: true,
        dataKinds: ["boolean_state", "categorical_enum", "string_label"],
        hint: "One or more state keys (online/offline, healthy/degraded, a status label or small enum). Leave empty to show all devices.",
      },
    ],
    loading: whileLoading("status-grid", "Device status"),
    render: ({ title, subtitle, dataSource, liveTagData }) => (
      <ChartCard title={title ?? "Device status"} subtitle={subtitle}>
        {isLiveApi ? (
          <StatusGrid keys={dataSource ? readSlot(dataSource, "state") : []} liveTagData={liveTagData} />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {FX_DEVICES.slice(0, 6).map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between rounded-md border border-border bg-surface-elevated p-2 text-xs"
              >
                <span className="truncate font-medium text-on-surface">
                  {d.name}
                </span>
                <span
                  className={`size-2 shrink-0 rounded-full ${
                    d.status === "online"
                      ? "bg-success"
                      : d.status === "warning"
                        ? "bg-warning"
                        : d.status === "offline"
                          ? "bg-on-surface-muted"
                          : "bg-danger"
                  }`}
                  aria-label={d.status}
                />
              </div>
            ))}
          </div>
        )}
      </ChartCard>
    ),
  },
  "defect-pareto": {
    type: "defect-pareto",
    name: "Defect Pareto",
    description: "Foundry pack — defects sorted by frequency with cumulative %.",
    icon: Activity,
    category: "industrial",
    defaultSize: { w: 6, h: 4 },
    accepts: [
      {
        id: "categories",
        label: "Defect distribution",
        role: "categories",
        required: false,
        multi: false,
        dataKinds: ["categorical_distribution", "count_scalar", "categorical_enum"],
        hint: "Defect type → count for a heat / shift / day. A defect-count or category key is also accepted.",
      },
    ],
    loading: null,
    render: ({ title, subtitle, range, dataWindow }) => (
      <DefectParetoWidget title={title} subtitle={subtitle} range={range} dataWindow={dataWindow} />
    ),
  },
  table: {
    type: "table",
    name: "Table",
    description: "Sortable + filterable tabular view.",
    icon: TableIcon,
    category: "table",
    defaultSize: { w: 6, h: 5 },
    accepts: [
      {
        id: "columns",
        label: "Columns",
        role: "series",
        required: false,
        multi: true,
        // Tables are intentionally permissive — strings, enums, scalars all OK.
        dataKinds: [
          "analog_scalar",
          "analog_percentage",
          "count_scalar",
          "derived_kpi",
          "categorical_enum",
          "boolean_state",
          "string_label",
        ],
        hint: "One column per key. Type-checking is advisory.",
      },
    ],
    loading: whileLoading("table", "Tag readings"),
    render: ({ title, subtitle, dataSource, importedRows, liveTagData, range, dataWindow }) => {
      // When the report is bound to an imported batch dataset, render
      // the top-problem-parts attribution. The existing recent-heats
      // table only applies to fixture data.
      if (importedRows && importedRows.length > 0) {
        const parts = aggregateTopProblemParts(importedRows, 20);
        const partColumns: ColumnDef<(typeof parts)[number]>[] = [
          {
            id: "part",
            header: "Part",
            accessor: (p) => p.partNumber,
            cell: (p) => (
              <span className="font-mono font-medium text-on-surface">{p.partNumber}</span>
            ),
          },
          {
            id: "good",
            header: "Good",
            accessor: (p) => p.goodTotal,
            align: "right",
            cell: (p) => <span className="font-mono">{p.goodTotal.toLocaleString()}</span>,
          },
          {
            id: "scrap",
            header: "Scrap",
            accessor: (p) => p.scrapTotal,
            align: "right",
            cell: (p) => <span className="font-mono">{p.scrapTotal.toLocaleString()}</span>,
          },
          {
            id: "scrap_pct",
            header: "Scrap %",
            accessor: (p) => p.scrapPct,
            align: "right",
            // GATE-SCOPE7: an uncounted part shows "—" with no verdict colour. Bare
            // `null > 10` is false, so the old chain would have inked it in the
            // "everything is fine" class while printing "0.00 %".
            cell: (p) => (
              <span
                className={
                  p.scrapPct == null
                    ? "font-mono text-on-surface-muted"
                    : p.scrapPct > 10
                      ? "font-mono font-semibold text-danger"
                      : p.scrapPct > 5
                        ? "font-mono text-warning"
                        : "font-mono text-on-surface"
                }
              >
                {p.scrapPct == null ? "—" : `${p.scrapPct.toFixed(2)} %`}
              </span>
            ),
          },
          {
            id: "lots",
            header: "Lots",
            accessor: (p) => p.lots,
            align: "right",
            cell: (p) => <span className="font-mono">{p.lots}</span>,
          },
          {
            id: "main_defects",
            header: "Main defects",
            accessor: (p) => p.mainDefects.join(", "),
            cell: (p) => (
              <span className="text-[11px] text-on-surface-muted">
                {p.mainDefects.length > 0 ? p.mainDefects.join(", ") : "—"}
              </span>
            ),
          },
        ];
        return (
          <ChartCard
            title={title ?? "Top problem parts"}
            subtitle={subtitle ?? `${parts.length} parts ranked by scrap pieces`}
          >
            <Table
              getRowId={(p) => p.partNumber}
              columns={partColumns}
              data={parts}
              hideSearch
              pageSize={20}
            />
          </ChartCard>
        );
      }

      // Live mode: build a snapshot table from the widget's bound tags (the
      // "columns" slot). One row per tag — latest value + unit + when it last
      // arrived. This is the sparse-friendly view a timeseries chart can't give:
      // an instrument that reports a few times a day shows its latest reading as
      // a legible row instead of two lonely dots on a line. Falls to an honest
      // empty state only when nothing is bound / resolved.
      if (isLiveApi) {
        const boundKeys = dataSource ? readSlot(dataSource, "columns") : [];

        // HISTORICAL mode — when the user pins a range, flatten every bound
        // tag's windowed series into one row per (tag, bucket-time), newest
        // first + paginated. This is the "show ALL the data" view the
        // latest-per-tag snapshot below can't give. The series is bucket-
        // aggregated by the backend (no raw rows) — expected + fine. A pinned window of days is history too (#994 review).
        if (range || dataWindow) {
          const histRows = boundKeys.flatMap((k) => {
            const d = liveTagData?.[k.toLowerCase()];
            if (!d) return [];
            return d.series
              .filter((p) => p.v != null)
              .map((p) => ({
                key: `${d.tagId}@${p.t}`,
                time: p.t,
                reading: readingName(d),
                machine: d.machine ?? "",
                value: p.v as number,
                unit: d.unit,
              }));
          });
          // Newest first. The Table keeps this order until a header is clicked.
          histRows.sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0));
          if (histRows.length > 0) {
            const histColumns: ColumnDef<(typeof histRows)[number]>[] = [
              {
                id: "time",
                header: "Time",
                accessor: (r) => r.time,
                cell: (r) => (
                  <span className="whitespace-nowrap text-[11px] text-on-surface-muted">
                    <PlantClock iso={r.time} />
                  </span>
                ),
              },
              {
                id: "reading",
                header: "Reading",
                accessor: (r) => r.reading,
                cell: (r) => <ReadingCell reading={r.reading} machine={r.machine} />,
              },
              {
                id: "value",
                header: "Value",
                accessor: (r) => r.value,
                align: "right",
                cell: (r) => (
                  <span className="font-mono text-on-surface">
                    {r.value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </span>
                ),
              },
              {
                id: "unit",
                header: "Unit",
                accessor: (r) => r.unit,
                cell: (r) => <span className="text-on-surface-muted">{r.unit || "—"}</span>,
              },
            ];
            return (
              <ChartCard
                title={title ?? "Tag history"}
                subtitle={
                  subtitle ??
                  `${histRows.length} reading${histRows.length === 1 ? "" : "s"} · ${boundKeys.length} tag${boundKeys.length === 1 ? "" : "s"}`
                }
              >
                <Table
                  getRowId={(r) => r.key}
                  columns={histColumns}
                  data={histRows}
                  hideSearch
                  pageSize={25}
                />
              </ChartCard>
            );
          }
          return (
            <ChartCard title={title ?? "Tag history"} subtitle={subtitle}>
              <WidgetEmpty
                icon={TableIcon}
                message="No readings in range"
                hint="No bucketed readings in the selected window. Widen the range, or bind tags that have data."
              />
            </ChartCard>
          );
        }

        const tagRows = boundKeys
          .map((k) => liveTagData?.[k.toLowerCase()])
          .filter((d): d is NonNullable<typeof d> => Boolean(d))
          .map((d) => ({
            key: d.tagId,
            reading: readingName(d),
            machine: d.machine ?? "",
            value: d.latest,
            unit: d.unit,
            updated: d.latestTime,
          }));
        if (tagRows.length > 0) {
          const tagColumns: ColumnDef<(typeof tagRows)[number]>[] = [
            {
              id: "reading",
              header: "Reading",
              accessor: (r) => r.reading,
              cell: (r) => <ReadingCell reading={r.reading} machine={r.machine} />,
            },
            {
              id: "value",
              header: "Value",
              accessor: (r) => r.value ?? Number.NEGATIVE_INFINITY,
              align: "right",
              cell: (r) => (
                <span className="font-mono text-on-surface">
                  {r.value == null
                    ? "—"
                    : r.value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                </span>
              ),
            },
            {
              id: "unit",
              header: "Unit",
              accessor: (r) => r.unit,
              cell: (r) => <span className="text-on-surface-muted">{r.unit || "—"}</span>,
            },
            {
              id: "updated",
              header: "Updated",
              accessor: (r) => r.updated ?? "",
              cell: (r) => (
                <span className="whitespace-nowrap text-[11px] text-on-surface-muted">
                  {r.updated ? <PlantClock iso={r.updated} /> : "—"}
                </span>
              ),
            },
          ];
          return (
            <ChartCard
              title={title ?? "Tag readings"}
              subtitle={subtitle ?? `${tagRows.length} tag${tagRows.length === 1 ? "" : "s"}`}
            >
              <Table
                getRowId={(r) => r.key}
                columns={tagColumns}
                data={tagRows}
                hideSearch
                hidePagination
              />
            </ChartCard>
          );
        }
        return (
          <ChartCard title={title ?? "Tag readings"} subtitle={subtitle}>
            <WidgetEmpty
              icon={TableIcon}
              message="No rows"
              hint="Bind tags to this table's columns to see their latest readings."
            />
          </ChartCard>
        );
      }
      const columns: ColumnDef<(typeof FX_BATCHES)[number]>[] = [
        {
          id: "heat",
          header: "Heat",
          accessor: (b) => b.heatNumber,
          cell: (b) => <span className="font-mono">{b.heatNumber}</span>,
        },
        {
          id: "grade",
          header: "Grade",
          accessor: (b) => b.grade,
          cell: (b) => <span className="font-mono">{b.grade}</span>,
        },
        {
          id: "status",
          header: "Status",
          accessor: (b) => b.status,
          cell: (b) => (
            <span
              className={
                b.status === "pass" ? "text-success" : "text-danger"
              }
            >
              {b.status.toUpperCase()}
            </span>
          ),
        },
        {
          id: "defect",
          header: "Defect",
          accessor: (b) => b.defectType ?? "",
          cell: (b) =>
            b.defectType ? (
              <span className="text-on-surface-muted">{b.defectType}</span>
            ) : (
              "—"
            ),
        },
      ];
      return (
        <ChartCard title={title ?? "Recent heats"} subtitle={subtitle}>
          <Table
            getRowId={(b) => b.heatNumber}
            columns={columns}
            data={FX_BATCHES.slice(0, 6)}
            hideSearch
            hidePagination
          />
        </ChartCard>
      );
    },
  },
  "correlation-heatmap": {
    type: "correlation-heatmap",
    name: "Correlation heatmap",
    description: "Pearson r between selected parameters.",
    icon: Sparkles,
    category: "industrial",
    defaultSize: { w: 6, h: 6 },
    accepts: [
      {
        id: "matrix",
        label: "Parameters",
        role: "matrix",
        required: true,
        multi: true,
        dataKinds: ["analog_scalar", "analog_percentage", "count_scalar"],
        hint: "Pick 2+ numeric parameters; r is computed across heats.",
      },
    ],
    loading: null,
    render: ({ title, subtitle }) => (
      <CorrelationHeatmapWidget title={title} subtitle={subtitle} />
    ),
  },
  gauge: {
    type: "gauge",
    name: "Gauge",
    description: "Radial gauge with target band — for single in/out-of-spec metrics.",
    icon: Activity,
    category: "kpi",
    defaultSize: { w: 3, h: 4 },
    accepts: [
      {
        id: "value",
        label: "Value",
        role: "value",
        required: true,
        multi: false,
        dataKinds: ["analog_scalar", "analog_percentage"],
        hint: "Bounded numeric. Percentages auto-scale to 0–100.",
      },
    ],
    loading: whileLoading("gauge", "Gauge"),
    render: ({ title, subtitle, dataSource, config, liveTagData }) => {
      const k = (readSlot(dataSource, "value")[0] ?? "").toLowerCase();
      // Per-widget fixed axis bounds override the auto-derived gauge range.
      const yMin = typeof config?.yMin === "number" ? config.yMin : undefined;
      const yMax = typeof config?.yMax === "number" ? config.yMax : undefined;
      // Live mode: drive the gauge off the latest reading; derive a range from
      // the recent series (no spec bands available for an arbitrary tag → one
      // neutral band across the observed range).
      const liveG = liveTagData?.[k];
      if (liveG && liveG.latest != null) {
        const vals = observedValues(liveG.series);   // a missing reading is a gap, never a 0 that widens the range
        let lo = vals.length ? Math.min(...vals) : liveG.latest;
        let hi = vals.length ? Math.max(...vals) : liveG.latest;
        const pad = (hi - lo) * 0.15 || Math.abs(liveG.latest) * 0.1 || 1;
        lo -= pad;
        hi += pad;
        if (hi <= lo) hi = lo + 1;
        // Fixed bounds win over the auto-derived range when the user sets them.
        if (yMin !== undefined) lo = yMin;
        if (yMax !== undefined) hi = yMax;
        if (hi <= lo) hi = lo + 1;
        const r2 = (n: number) => Math.round(n * 100) / 100;
        return (
          <ChartCard title={title ?? "Gauge"} subtitle={subtitle}>
            <div className="flex h-full items-center justify-center">
              <GaugeChart
                value={r2(liveG.latest)}
                min={r2(lo)}
                max={r2(hi)}
                unit={liveG.unit}
                bands={[{ from: r2(lo), to: r2(hi), kind: "ok" as const }]}
              />
            </div>
          </ChartCard>
        );
      }
      // Live mode + a bound key with no readings → honest empty, not a fixture gauge.
      if (isLiveApi && k) {
        return (
          <ChartCard title={title ?? "Gauge"} subtitle={subtitle}>
            <WidgetEmpty icon={Activity} {...noReadingsWords(liveTagData, [k])} />
          </ChartCard>
        );
      }
      const cfg = k.includes("moisture")
        ? { value: 3.92, min: 2, max: 6, unit: "%", bands: [
            { from: 3.4, to: 4.2, kind: "ok" as const },
            { from: 4.2, to: 4.8, kind: "watch" as const },
            { from: 4.8, to: 6, kind: "warn" as const },
          ] }
        : k.includes("bath") || k.includes("temp")
          ? { value: 1438, min: 1300, max: 1550, unit: "°C", bands: [
              { from: 1380, to: 1480, kind: "ok" as const },
              { from: 1480, to: 1500, kind: "watch" as const },
              { from: 1500, to: 1550, kind: "danger" as const },
            ] }
          : { value: 70, min: 0, max: 100, unit: "%", bands: [
              { from: 0, to: 60, kind: "warn" as const },
              { from: 60, to: 90, kind: "ok" as const },
              { from: 90, to: 100, kind: "watch" as const },
            ] };
      // Fixed bounds override the demo gauge's min/max; bands are clamped to
      // the visible range by GaugeChart itself. Guard against an inverted
      // range (e.g. min set above the default max) so the sweep math stays valid.
      const gMin = yMin ?? cfg.min;
      let gMax = yMax ?? cfg.max;
      if (gMax <= gMin) gMax = gMin + 1;
      return (
        <ChartCard title={title ?? "Gauge"} subtitle={subtitle}>
          <div className="flex h-full items-center justify-center">
            <GaugeChart {...cfg} min={gMin} max={gMax} />
          </div>
        </ChartCard>
      );
    },
  },
  pareto: {
    type: "pareto",
    name: "Pareto chart",
    description: "Bars sorted by count + cumulative-% overlay.",
    icon: Activity,
    category: "categorical",
    defaultSize: { w: 6, h: 4 },
    accepts: [
      {
        id: "categories",
        label: "Distribution",
        role: "categories",
        required: false,
        multi: false,
        // `categorical_distribution` is the ideal kind, but `count_scalar` /
        // `categorical_enum` keys are also bindable so the picker isn't a
        // dead-end — a count tag is bucketed into the Pareto by the resolver.
        dataKinds: ["categorical_distribution", "count_scalar", "categorical_enum"],
        hint: "Named buckets with counts (defects by type, alarms by source). A count or category key works too.",
      },
    ],
    loading: null,
    render: ({ title, subtitle, importedRows }) => {
      const hasImported = Boolean(importedRows && importedRows.length > 0);
      const data = hasImported
        ? aggregateDefectPareto(importedRows!).map((d) => ({
            name: d.defect,
            count: d.count,
          }))
        : FX_DEFECT_PARETO;
      // Live mode with no imported distribution → honest empty state.
      if (!hasImported && isLiveApi) {
        return (
          <ChartCard title={title ?? "Pareto"} subtitle={subtitle}>
            <WidgetEmpty
              icon={Activity}
              message="No distribution data"
              hint="Bind a categorical distribution (e.g. defects by type), or import a batch dataset, to populate the Pareto."
            />
          </ChartCard>
        );
      }
      return (
        <ChartCard title={title ?? "Pareto"} subtitle={subtitle}>
          <ParetoChart data={data} height={220} />
        </ChartCard>
      );
    },
  },
  spc: {
    type: "spc",
    name: "SPC chart",
    description:
      "A statistical process control (SPC) chart of the reading: the control limits the plant analysis set from the process's own history, and the signals it found.",
    icon: Activity,
    category: "statistical",
    defaultSize: { w: 6, h: 4 },
    accepts: [
      {
        id: "value",
        label: "Sample value",
        role: "value",
        required: true,
        multi: false,
        dataKinds: ["analog_scalar", "analog_percentage"],
        hint: "A numeric tag. The plant analysis picks its chart and sets the control limits.",
      },
    ],
    loading: whileLoading("spc", "SPC"),
    render: ({ title, subtitle, dataSource, config, liveTagData }) => {
      const k = (readSlot(dataSource, "value")[0] ?? "bath_temp").toLowerCase();
      const yMin = typeof config?.yMin === "number" ? config.yMin : undefined;
      const yMax = typeof config?.yMax === "number" ? config.yMax : undefined;
      const liveS = liveTagData?.[k];
      const hasLive = Boolean(liveS && liveS.series.length > 0);
      // Live mode with no readings → honest empty state, not a fixture SPC run.
      if (isLiveApi && !hasLive) {
        return (
          <ChartCard title={title ?? "SPC"} subtitle={subtitle}>
            <WidgetEmpty icon={Activity} {...noReadingsWords(liveTagData, [k])} />
          </ChartCard>
        );
      }
      // Live series wins; else a deterministic sample from fixtures (stub).
      const src = hasLive
        ? liveS!.series
        : k.includes("moisture")
          ? FX_SAND_MOISTURE.points
          : FX_BATH_TEMP.points;
      // The last 40 observations, plotted without limits when the tag has no control chart: a missing reading
      // is left out, never drawn as 0.
      const points = lastObservations(src, 40);
      // Compare-with-ideal (QC §3.5): a stored golden reference {mean,sigma} on
      // the widget config overlays the SPC tile. The qc API (lib/api/qc.ts)
      // builds + manages these curves; here we render a frozen one if present.
      const refCfg = (config as Record<string, unknown> | undefined)?.reference;
      const reference =
        refCfg &&
        typeof refCfg === "object" &&
        typeof (refCfg as { mean?: unknown }).mean === "number" &&
        typeof (refCfg as { sigma?: unknown }).sigma === "number"
          ? (refCfg as { mean: number; sigma: number; bands?: 1 | 2 | 3 })
          : undefined;
      // The bound tag's control chart is the server's (spc-widget.tsx): its limits, signals and episodes.
      return (
        <ChartCard title={title ?? "SPC"} subtitle={subtitle}>
          <SpcTagChart
            tagId={liveS?.tagId}
            unit={liveS?.unit || undefined}
            storedUnit={liveS?.storedUnit}
            points={points}
            height={220}
            yMin={yMin}
            yMax={yMax}
            reference={reference}
          />
        </ChartCard>
      );
    },
  },
  histogram: {
    type: "histogram",
    name: "Histogram",
    description: "Frequency distribution — drift + bi-modal detection.",
    icon: Activity,
    category: "statistical",
    defaultSize: { w: 6, h: 4 },
    accepts: [
      {
        id: "value",
        label: "Value",
        role: "value",
        required: true,
        multi: false,
        dataKinds: ["analog_scalar", "analog_percentage", "count_scalar"],
        hint: "Numeric scalar to bin. Categorical / label data won't work.",
      },
    ],
    loading: whileLoading("histogram", "Histogram"),
    render: ({ title, subtitle, dataSource, config, liveTagData, bands }) => {
      const k = (readSlot(dataSource, "value")[0] ?? "").toLowerCase();
      const yMin = typeof config?.yMin === "number" ? config.yMin : undefined;
      const yMax = typeof config?.yMax === "number" ? config.yMax : undefined;
      const liveH = liveTagData?.[k];
      // A band (B6: the machine's yardstick, or the card's ends) draws its ends; without one, the data's own reach.
      const band = bands?.[k];
      const isLive = Boolean(liveH && liveH.series.length > 0);
      // Live mode with no readings → honest empty state, not a fixture histogram.
      if (isLiveApi && !isLive) {
        return (
          <ChartCard title={title ?? "Histogram"} subtitle={subtitle}>
            <WidgetEmpty icon={Activity} {...noReadingsWords(liveTagData, [k])} />
          </ChartCard>
        );
      }
      const values = isLive
        ? observedValues(liveH!.series)   // no fabricated bar at 0 for missing readings
        // S2.3b: `pourTemp` is now `number | null` (an unmeasured pour temp used to
        // be mapped to 0). DROP the unmeasured heats rather than binning them — a
        // 0 °C bar in a pour-temp histogram is a fabricated observation, and it
        // would also drag the auto-scaled axis to zero.
        : FX_BATCHES.map((b) => b.pourTemp).filter((v): v is number => v !== null);
      const tMin = band ? band.lo : isLive ? Math.min(...values) : 1370;
      const tMax = band ? band.hi : isLive ? Math.max(...values) : 1410;
      const bandWords = band && isLive ? bandText(band, liveH!.unit) : null;
      return (
        <ChartCard
          title={title ?? "Histogram"}
          subtitle={withSentence(subtitle, bandWords)}
        >
          <HistogramChart
            values={values}
            targetMin={tMin}
            targetMax={tMax}
            xLabel={isLive ? readingName(liveH!) : "Pour temp"}
            unit={isLive ? liveH!.unit : "°C"}
            yMin={yMin}
            yMax={yMax}
            height={220}
          />
        </ChartCard>
      );
    },
  },
  oee: {
    type: "oee",
    name: "OEE panel",
    description: "Overall Equipment Effectiveness — availability × performance × quality.",
    icon: Activity,
    category: "industrial",
    defaultSize: { w: 8, h: 5 },
    accepts: [], // Composite derived metric — no user-bound slot in Phase 0.
    loading: null,
    render: ({ title, subtitle }) => (
      <ChartCard title={title ?? "OEE"} subtitle={subtitle}>
        {isLiveApi ? (
          <WidgetEmpty
            icon={Activity}
            message="No OEE data"
            hint="OEE needs availability, performance + quality tags bound. Connect a source to compute it."
          />
        ) : (
          <OEEPanel
            data={{
              availability: 86.4,
              performance: 79.1,
              quality: 96.1,
              history: syntheticOEEHistory(),
              shiftLabel: "Shift B · current",
            }}
          />
        )}
      </ChartCard>
    ),
  },
  scatter: {
    type: "scatter",
    name: "Scatter plot",
    description: "Pair plot — one parameter against another across heats.",
    icon: Activity,
    category: "statistical",
    defaultSize: { w: 6, h: 4 },
    accepts: [
      {
        id: "x",
        label: "X axis",
        role: "x",
        required: true,
        multi: false,
        dataKinds: ["analog_scalar", "analog_percentage"],
        hint: "Independent variable plotted on the horizontal axis.",
      },
      {
        id: "y",
        label: "Y axis",
        role: "y",
        required: true,
        multi: false,
        dataKinds: ["analog_scalar", "analog_percentage"],
        hint: "Dependent variable plotted on the vertical axis.",
      },
    ],
    loading: whileLoading("scatter", "Scatter"),
    render: ({ title, subtitle, dataSource, config, liveTagData }) => {
      const xKey = readSlot(dataSource, "x")[0] ?? "pour_temperature";
      const yKey = readSlot(dataSource, "y")[0] ?? "ultimate_tensile_strength";
      const yMin = typeof config?.yMin === "number" ? config.yMin : undefined;
      const yMax = typeof config?.yMax === "number" ? config.yMax : undefined;
      // Live mode: pair the two bound tags' series BY TIME (nearest sample within
      // half a step), never index-by-index — two tags logged at different rates
      // would otherwise be plotted against unrelated moments, and a gap would
      // become a zero. No fixture fallback — an unbound widget shows an empty
      // state, not the demo heats.
      if (isLiveApi) {
        const xd = liveTagData?.[xKey.toLowerCase()];
        const yd = liveTagData?.[yKey.toLowerCase()];
        const livePoints = pairSeriesByTime(xd?.series ?? [], yd?.series ?? []);
        // Each axis names its reading as the machine page does (reading-names.ts); with no reading, the key's words.
        const [xName, yName] =
          xd && yd ? chartReadingNames([xd, yd]) : [xKey.replace(/_/g, " "), yKey.replace(/_/g, " ")];
        return (
          <ChartCard title={title ?? "Scatter"} subtitle={subtitle}>
            {livePoints.length === 0 ? (
              <WidgetEmpty icon={Activity} {...noReadingsWords(liveTagData, [xKey, yKey])} />
            ) : (
              <ScatterPlot
                xLabel={xName}
                yLabel={yName}
                points={livePoints}
                showTrendline
                yMin={yMin}
                yMax={yMax}
                height={220}
              />
            )}
          </ChartCard>
        );
      }
      // Stub fixture: pull pairs from FX_BATCHES so the binding is visibly
      // type-correct.
      // S2.3b: both axes are now `number | null`. A point needs BOTH coordinates to
      // exist — a heat with a pour temp and no moisture is not a point at (t, 0),
      // and plotting it there would also feed a false pair to the trendline.
      const points = FX_BATCHES.slice(0, 80)
        .filter((b) => b.pourTemp !== null && b.moisture !== null)
        .map((b) => ({
          x: b.pourTemp as number,
          y: b.moisture as number,
          category: b.status === "pass" ? ("pass" as const) : ("fail" as const),
          id: b.heatNumber,
        }));
      return (
        <ChartCard title={title ?? "Scatter"} subtitle={subtitle}>
          <ScatterPlot
            xLabel={xKey.replace(/_/g, " ")}
            yLabel={yKey.replace(/_/g, " ")}
            points={points}
            showTrendline
            yMin={yMin}
            yMax={yMax}
            height={220}
          />
        </ChartCard>
      );
    },
  },
  "equipment-overview": {
    type: "equipment-overview",
    name: "Equipment overview",
    description: "A live composite card for one equipment — all its sensor ports + parameters as a unit, fed by /port-values.",
    icon: Boxes,
    category: "industrial",
    defaultSize: { w: 4, h: 5 },
    accepts: [], // resolves its entity from config.entityId / alias / first equipment
    loading: null,
    render: ({ title, subtitle, config, dataSource }) => (
      <EquipmentOverviewCard
        title={title}
        subtitle={subtitle}
        config={config}
        dataSource={dataSource}
      />
    ),
  },
  markdown: {
    type: "markdown",
    name: "Notes",
    description: "Static text / markdown panel.",
    icon: Activity,
    category: "content",
    defaultSize: { w: 4, h: 3 },
    accepts: [], // Static content — no data binding.
    loading: null,
    render: ({ title, config }) => (
      <ChartCard title={title}>
        <p className="text-sm text-on-surface-muted">
          {(config?.body as string) ?? "Add notes here…"}
        </p>
      </ChartCard>
    ),
  },
};

// ---- shared helpers --------------------------------------------------

/** A chart card with its title, and the chart's outline in the place of its data. */
function whileLoading(shape: SkeletonShape, defaultTitle?: string) {
  return function ChartLoading({ title, subtitle, keys }: LoadingProps) {
    return (
      <ChartCard title={title ?? defaultTitle} subtitle={subtitle}>
        <ChartSkeleton shape={shape} count={keys} />
      </ChartCard>
    );
  };
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title?: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col">
      {(title || subtitle) && (
        <div className="mb-2 flex flex-col gap-0.5">
          {title && (
            <h3 className="text-sm font-semibold text-on-surface">{title}</h3>
          )}
          {subtitle && (
            <p className="text-[11px] text-on-surface-muted">{subtitle}</p>
          )}
        </div>
      )}
      {/* The chart fills the tile. `[&>*]:!h-full` forces the chart's own root
          element to height:100% (overriding its intrinsic `height={…}` prop via
          !important > inline style), so a SHORT grid cell shrinks the chart to
          fit instead of overflowing — which previously let overflow-hidden clip
          the bottom of the plot (axis baseline + lowest values, e.g. a 0–100
          axis cutting off data near 0). min-h-0 lets the flex child shrink. */}
      <div className="min-h-0 flex-1 overflow-hidden [&>*]:!h-full">{children}</div>
    </div>
  );
}

/** A reading's time on the plant's clock, to the minute (reading-names.ts plantClockText). */
function PlantClock({ iso }: { iso: string }) {
  const plantTime = useQuery({
    queryKey: ["plant-time"],
    queryFn: plantTimeApi.get,
    enabled: isLiveApi,
    staleTime: 5 * 60_000,
  });
  return <>{plantClockText(iso, plantTime.data?.timezone)}</>;
}

/** A table's reading: its name in the plant's words, and the machine it is read on under it. */
function ReadingCell({ reading, machine }: { reading: string; machine: string }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="font-medium text-on-surface">{reading}</span>
      {machine && <span className="text-[11px] text-on-surface-muted">{machine}</span>}
    </span>
  );
}

/**
 * Compact in-widget empty state — fits a dashboard tile (unlike the page-scale
 * `EmptyState`). Used when a widget has no live source / no data in live mode,
 * so the dashboard shows an honest "nothing here yet" instead of fixtures.
 */
export function WidgetEmpty({
  icon: Icon = Inbox,
  message,
  hint,
}: {
  icon?: typeof Activity;
  message: string;
  hint?: string;
}) {
  return (
    <div className="flex h-full min-h-[80px] flex-col items-center justify-center gap-1.5 px-4 text-center">
      <Icon className="size-5 text-on-surface-muted/60" strokeWidth={1.5} />
      <p className="text-xs font-medium text-on-surface-muted">{message}</p>
      {hint && <p className="max-w-[28ch] text-[11px] text-on-surface-muted/70">{hint}</p>}
    </div>
  );
}

/**
 * Alarm-list widget. Live mode: reads the org's real alarm events from the
 * app-wide AlarmsProvider (fed by `/v1/iot/alarms/events`); empty state when
 * none. Stub mode: the fixture alarms, for local UI dev.
 */
function AlarmListWidget({
  title,
  subtitle,
  limit,
}: {
  title?: string;
  subtitle?: string;
  limit: number;
}) {
  const alarms = useOptionalAlarms();
  if (isLiveApi) {
    const events = alarms?.events ?? [];
    return (
      <ChartCard title={title ?? "Alarms"} subtitle={subtitle}>
        {alarms?.eventsLoading ? (
          <ChartSkeleton shape="alarms" />
        ) : events.length === 0 ? (
          <WidgetEmpty
            icon={Bell}
            message="No alarms"
            hint="Alarms appear here when a rule fires on a tag."
          />
        ) : (
          <AlarmList alarms={events.slice(0, limit)} onAcknowledge={() => {}} />
        )}
      </ChartCard>
    );
  }
  return (
    <ChartCard title={title ?? "Alarms"} subtitle={subtitle}>
      <AlarmList alarms={FX_ALARMS.slice(0, limit)} onAcknowledge={() => {}} />
    </ChartCard>
  );
}

/**
 * Event-feed widget. Live mode: derives recent activity from the org's real
 * alarm events (AlarmItem extends EventItem); empty state when none. Stub
 * mode: the fixture event log.
 */
function EventFeedWidget({
  title,
  subtitle,
  limit,
}: {
  title?: string;
  subtitle?: string;
  limit: number;
}) {
  const alarms = useOptionalAlarms();
  if (isLiveApi) {
    const events = alarms?.events ?? [];
    return (
      <ChartCard title={title ?? "Recent activity"} subtitle={subtitle}>
        {alarms?.eventsLoading ? (
          <ChartSkeleton shape="events" />
        ) : events.length === 0 ? (
          <WidgetEmpty
            icon={Inbox}
            message="No recent activity"
            hint="Events appear here as alarms fire."
          />
        ) : (
          <EventFeed events={events} limit={limit} />
        )}
      </ChartCard>
    );
  }
  return (
    <ChartCard title={title ?? "Recent activity"} subtitle={subtitle}>
      <EventFeed events={FX_EVENTS} limit={limit} />
    </ChartCard>
  );
}

/**
 * Correlation-heatmap widget. Live mode: the real Pearson matrix from
 * `/v1/twin/correlations` (the same source the Correlations page uses), with
 * an honest empty state until enough heats accumulate. Stub mode: the fixture
 * matrix for local UI dev.
 */
function CorrelationHeatmapWidget({
  title,
  subtitle,
}: {
  title?: string;
  subtitle?: string;
}) {
  // Hook is unconditional (disabled in stub mode via the query's enabled gate).
  const q = useTwinCorrelationsQuery();
  if (isLiveApi) {
    const cells = q.data?.cells ?? [];
    return (
      <ChartCard title={title ?? "Correlations"} subtitle={subtitle}>
        {q.isLoading ? (
          <ChartSkeleton shape="heatmap" />
        ) : cells.length === 0 ? (
          <WidgetEmpty
            icon={Sparkles}
            message="No correlations yet"
            hint="There are not enough readings yet to see how they move together."
          />
        ) : (
          <div className="-mx-3">
            <CorrelationHeatmap
              parameters={q.data!.params}
              labels={q.data!.labels}
              cells={cells}
            />
          </div>
        )}
      </ChartCard>
    );
  }
  return (
    <ChartCard title={title ?? "Correlations"} subtitle={subtitle}>
      <div className="-mx-3">
        <CorrelationHeatmap
          parameters={FX_CORRELATION_PARAMS}
          labels={FX_CORRELATION_LABELS}
          cells={FX_CORRELATION_CELLS}
        />
      </div>
    </ChartCard>
  );
}

/**
 * Defect-Pareto widget. Live mode: the defect mix derived from the org's real
 * heats (`useTwinAggregates`, same heat source as Batches + Reports), with an
 * honest empty state until heats accumulate — NO spreadsheet import needed.
 * Stub mode: the fixture defect mix for the backend-less demo.
 */
function DefectParetoWidget({
  title,
  subtitle,
  range,
  dataWindow,
}: {
  title?: string;
  subtitle?: string;
  range?: RangeToken;
  dataWindow?: WidgetWindow;
}) {
  const agg = useTwinAggregates(dataWindow);
  // A plant that books its scrap on casting lots: the scrapped pieces per defect booked in the widget's window
  // (lot-aggregates.ts), the same count the lot reports make.
  const lotAgg = useLotAggregates(dataWindow, range, agg.heatCount === 0 || agg.allHeatsImported);
  // While the heats, or the lots they defer to, load: say so, never "No defect data" first (#994 review, LOW-4).
  if (isLiveApi && (agg.isLoading || lotAgg.isLoading)) {
    return (
      <ChartCard title={title ?? "Scrap by defect"} subtitle={subtitle}>
        <ChartSkeleton shape="pareto" />
      </ChartCard>
    );
  }
  if (isLiveApi && lotsAnswer(agg, lotAgg.lots)) {
    const heading = title ?? `Scrap by defect, pieces${dataWindow ? ` · ${windowLabel(dataWindow)}` : ""}`;
    if (lotAgg.defectPareto.length === 0) {
      return (
        <ChartCard title={heading} subtitle={subtitle}>
          <WidgetEmpty icon={Activity} message="No scrap booked by defect in this window" />
        </ChartCard>
      );
    }
    return (
      <ChartCard title={heading} subtitle={subtitle}>
        <DefectParetoChart defects={lotAgg.defectPareto} height={220} />
      </ChartCard>
    );
  }
  if (isLiveApi) {
    if (agg.defectPareto.length === 0) {
      return (
        <ChartCard title={title ?? "Defects · last 30 days"} subtitle={subtitle}>
          <WidgetEmpty icon={Activity} message="No defect data" hint={noScrapWords(agg)} />
        </ChartCard>
      );
    }
    return (
      <ChartCard title={title ?? "Defects · last 30 days"} subtitle={subtitle}>
        <DefectParetoChart defects={agg.defectPareto} height={220} />
      </ChartCard>
    );
  }
  return (
    <ChartCard title={title ?? "Defects · last 30 days"} subtitle={subtitle}>
      <DefectParetoChart defects={FX_DEFECT_PARETO} height={220} />
    </ChartCard>
  );
}

/**
 * KPI widget. Priority: a bound live tag's latest reading wins (a real
 * sensor); else the metric is derived from the org's live heat roll-ups
 * (`useTwinAggregates` — yield %, rejection %, parts produced), so headline
 * KPIs work on a dashboard with no spreadsheet import. Honest empty state in
 * live mode when neither resolves; stub mode keeps the demo fixtures.
 */
function KpiWidget({
  title,
  subtitle,
  dataSource,
  liveTagData,
  range,
  dataWindow,
}: {
  title?: string;
  subtitle?: string;
  dataSource?: DataSource;
  liveTagData?: LiveTagData;
  range?: RangeToken;
  dataWindow?: WidgetWindow;
}) {
  const heatAgg = useTwinAggregates(dataWindow);
  // A plant that books its scrap on casting lots reads its roll-ups (scrap rate, pieces, lots) from the lots booked in
  // the widget's window, as the lot reports do (lot-aggregates.ts); every other plant from its heats.
  const lotsWanted = heatAgg.heatCount === 0 || heatAgg.allHeatsImported;
  const lotAgg = useLotAggregates(dataWindow, range, lotsWanted);
  const fromLots = isLiveApi && lotsAnswer(heatAgg, lotAgg.lots);
  const agg = fromLots ? lotAgg : heatAgg;
  const valueKey = (readSlot(dataSource, "value")[0] ?? "").toLowerCase();
  const liveK = valueKey ? liveTagData?.[valueKey] : undefined;
  const hasLiveTag = Boolean(liveK && liveK.latest != null);
  // A roll-up waits for the heats, and for the lots where the heats defer to them, so it never shows the heats' figure
  // and then flips to the lots'. A live tag's reading needs neither (#994 review, LOW-4).
  if (isLiveApi && !hasLiveTag && (heatAgg.isLoading || (lotsWanted && lotAgg.isLoading))) {
    return (
      <KPICard label={title ?? "Metric"} hint={subtitle} data={{ value: 0, unit: "" }} state={{ kind: "loading" }} className="h-full" />
    );
  }
  // Does the bound key map onto a live heat roll-up (yield / rejection / parts)?
  // Live mode only — stub mode keeps the demo fixture cards (with deltas) below.
  const resolvedKpi =
    isLiveApi && agg.heatCount > 0
      ? resolveTwinKpi(agg.kpi, valueKey || dataSource?.alias || "")
      : null;
  // On the lots, a count of "heats" is a count of lots.
  const twinKpi = fromLots && resolvedKpi?.unit === "heats" ? { ...resolvedKpi, unit: "lots" } : resolvedKpi;

  // GATE-SCOPE7: a roll-up that RESOLVED but has no denominator (nobody counted the
  // castings) is a THIRD state — not "unrecognised key", not "measured". It must take
  // the same honest-empty branch, because falling through to `resolveKpiData` would
  // hit the fixture ladder below and print a demo number on a live card.
  const twinKpiUnmeasured = Boolean(twinKpi && twinKpi.value == null);

  // Live mode with a bound key but no live tag AND no twin roll-up → honest
  // empty (the "old content" bug — never a fabricated fixture).
  if (isLiveApi && !hasLiveTag && ((valueKey && !twinKpi) || twinKpiUnmeasured)) {
    return (
      <KPICard
        label={title ?? "Metric"}
        hint={subtitle}
        data={{ value: 0, unit: "" }}
        state={{ kind: "empty" }}
        className="h-full"
      />
    );
  }

  const data = resolveKpiData(dataSource, twinKpi, agg, liveTagData);
  return (
    <KPICard label={title ?? "Metric"} hint={subtitle} data={data} className="h-full" />
  );
}

/**
 * Time-series widget. Live tag readings win (one line per bound tag). The
 * scrap / rejection branch is served by the plant's daily scrap trend: its
 * casting lots' where it books its scrap on them, else its heats'
 * (scrapTrendOf), so a "daily scrap %" line works with no spreadsheet. Honest
 * empty state in live mode when nothing resolves; stub mode keeps the fixture
 * series.
 */
function TimeseriesWidget({
  title,
  subtitle,
  dataSource,
  config,
  liveTagData,
  liveSpan,
  range,
  dataWindow,
  bands,
}: {
  title?: string;
  subtitle?: string;
  dataSource?: DataSource;
  config?: Record<string, unknown>;
  liveTagData?: LiveTagData;
  liveSpan?: LiveSpan;
  range?: RangeToken;
  dataWindow?: WidgetWindow;
  bands?: Record<string, WidgetBand>;
}) {
  const seriesKeys = readSlot(dataSource, "series");
  const keyLower = (seriesKeys[0] ?? "").toLowerCase();
  const wantsScrap = keyLower.includes("rejection") || keyLower.includes("scrap");
  const heatAgg = useTwinAggregates(dataWindow);
  // A scrap line reads the casting lots where the plant books its scrap on them, as the KPI and the Pareto do
  // (scrapTrendOf): a plant whose heats are all imported has no outcome on its heats, so their trend is a few stray
  // days. Only a scrap line fetches the lots.
  const lotsWanted = wantsScrap && (heatAgg.heatCount === 0 || heatAgg.allHeatsImported);
  const lotAgg = useLotAggregates(dataWindow, range, lotsWanted);
  const agg = isLiveApi
    ? { ...heatAgg, dailyScrapTrend: scrapTrendOf(heatAgg, lotAgg), isLoading: heatAgg.isLoading || (lotsWanted && lotAgg.isLoading) }
    : heatAgg;
  const variant = (config?.variant as "line" | "area") ?? "line";
  const yMin = typeof config?.yMin === "number" ? config.yMin : undefined;
  const yMax = typeof config?.yMax === "number" ? config.yMax : undefined;

  const hasLive = seriesKeys.some(
    (key) => (liveTagData?.[key.toLowerCase()]?.series.length ?? 0) > 0,
  );
  // A scrap line waits for its source, so it never draws the heats' few days and then flips to the lots' (the KPI
  // waits the same way). A bound tag's own readings need neither.
  if (isLiveApi && wantsScrap && !hasLive && agg.isLoading) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <ChartSkeleton shape={variant} count={seriesKeys.length} />
      </ChartCard>
    );
  }
  // The scrap/rejection branch can be served by the plant's heats or its casting lots (no live tag, no import): only
  // fall to the empty state when that source is empty too.
  const hasTwinScrap = wantsScrap && agg.dailyScrapTrend.length > 0;

  if (isLiveApi && seriesKeys.length > 0 && !hasLive && !hasTwinScrap) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <WidgetEmpty
          icon={LineChart}
          {...(wantsScrap
            ? { message: "No data", hint: noScrapWords(heatAgg, lotAgg.lots) }
            : noReadingsWords(liveTagData, seriesKeys))}
        />
      </ChartCard>
    );
  }

  const series = resolveTimeseries(dataSource, agg, liveTagData, bands);
  // A band drawn behind a line says what it is (B6: the machine's yardstick, or the card's ends).
  const bandWords = seriesKeys
    .map((key) => {
      const band = bands?.[key.toLowerCase()];
      const d = liveTagData?.[key.toLowerCase()];
      return band && d && d.series.length > 0 ? bandText(band, d.unit, "shaded") : null;
    })
    .filter((w): w is string => w !== null);
  const said = withSentence(subtitle, bandWords.join(" "));
  // Surface the value unit on the Y axis (#105). When every rendered series
  // shares one unit we pass it through; mixed units stay unlabelled.
  const yUnit = commonSeriesUnit(series);
  // Live readings run their time axis over the span they were read for; a lookback that counted back from the last
  // reading says so, as the reports' presets do.
  const span: [number, number] | undefined = hasLive && liveSpan ? [liveSpan.from, liveSpan.to] : undefined;
  const anchored = hasLive && liveSpan?.anchored
    ? `Readings to ${new Date(liveSpan.to).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}: the window counts back from the last reading.`
    : undefined;
  return (
    <ChartCard title={title} subtitle={withSentence(said, anchored)}>
      <TimeseriesChart
        series={series}
        variant={variant}
        height={220}
        yUnit={yUnit}
        yMin={yMin}
        yMax={yMax}
        span={span}
      />
    </ChartCard>
  );
}

/** Resolve a KPI data source — Phase 0 cheats by name-matching the alias.
 *  Live mode: a bound tag's reading wins; else a live heat roll-up (passed in
 *  as `twinKpi` by KpiWidget). Stub mode falls through to the demo fixtures. */
function resolveKpiData(
  ds: DataSource | undefined,
  twinKpi: { value: number | null; unit: string } | null,
  agg: TwinAggregates,
  liveTagData?: LiveTagData,
) {
  if (!ds) {
    return { value: 0, unit: "" };
  }
  const aliasLower = ds.alias.toLowerCase();
  const keyLower = (readSlot(ds, "value")[0] ?? "").toLowerCase();

  // Live readings win in live mode: the bound key is a real tag name, so show
  // its latest value + recent series as the sparkline trend.
  if (liveTagData) {
    const live = liveTagData[keyLower];
    if (live && live.latest != null) {
      const trendKey = (readSlot(ds, "trend")[0] ?? "").toLowerCase();
      const trendLive = liveTagData[trendKey] ?? live;
      return {
        value: Math.round(live.latest * 100) / 100,
        unit: live.unit,
        trend: trendLive.series.slice(-40),
      };
    }
  }

  // Live twin roll-up: the bound key maps onto a heat-derived KPI (yield %,
  // rejection %, parts produced). For a rejection/scrap metric we attach the
  // daily scrap-trend as the sparkline so the card carries a live trend.
  // `value != null` is load-bearing: an unmeasured roll-up is handled by the
  // honest-empty branch in the widget, and must never reach the fixture ladder
  // below wearing a demo number (GATE-SCOPE7).
  if (twinKpi && twinKpi.value != null) {
    const isRejection = keyLower.includes("rejection") || keyLower.includes("scrap");
    return {
      value: twinKpi.value,
      unit: twinKpi.unit,
      trend:
        isRejection && agg.dailyScrapTrend.length > 0
          ? agg.dailyScrapTrend.slice(-30)
          : undefined,
    };
  }
  if (keyLower.includes("bath") || keyLower.includes("temp")) {
    return {
      value: 1438,
      unit: "°C",
      trend: FX_BATH_TEMP.points.slice(-20),
      delta: { value: 18, direction: "up" as const, sentiment: "neutral" as const, label: "vs 15 min ago" },
    };
  }
  if (keyLower.includes("moisture")) {
    return {
      value: 3.91,
      unit: "%",
      trend: FX_SAND_MOISTURE.points.slice(-20),
      delta: { value: 0.08, direction: "up" as const, sentiment: "neutral" as const, label: "vs target band" },
    };
  }
  if (keyLower.includes("power")) {
    return {
      value: 415,
      unit: "kW",
      trend: FX_POWER_KW.points.slice(-20),
    };
  }
  if (aliasLower.includes("rejection") || keyLower.includes("rejection")) {
    return {
      value: 15.5,
      unit: "%",
      trend: FX_KPI_SPARK,
      delta: { value: 2.1, direction: "down" as const, sentiment: "positive" as const, label: "vs last week" },
    };
  }
  if (aliasLower.includes("device") || keyLower.includes("online")) {
    return {
      value: 6,
      unit: "of 8",
      delta: { value: 1, direction: "down" as const, sentiment: "negative" as const, label: "vs yesterday" },
    };
  }
  return { value: 124_900, unit: "msg", trend: FX_KPI_SPARK };
}

/**
 * The single shared unit across a set of series, or undefined when they
 * disagree (mixed units → no axis unit; the legend disambiguates) or none
 * declare one. Drives the timeseries widget's Y-axis unit (#105).
 */
function commonSeriesUnit(
  series: Array<{ unit?: string }>,
): string | undefined {
  const units = new Set(
    series.map((s) => s.unit).filter((u): u is string => Boolean(u)),
  );
  return units.size === 1 ? [...units][0] : undefined;
}

/** Resolve a Timeseries data source — Phase 0 cheats by key name match.
 *  Live mode: a bound tag's polled series wins; a scrap/rejection key is
 *  served by the daily scrap % the caller picked (the lots' or the heats',
 *  scrapTrendOf). Stub mode → fixtures. */
function resolveTimeseries(
  ds: DataSource | undefined,
  agg: TwinAggregates,
  liveTagData?: LiveTagData,
  bands?: Record<string, WidgetBand>,
) {
  // Live readings win in live mode: one series per bound tag, from the
  // polled /values time-series, each with its band behind it (B6).
  if (ds && liveTagData) {
    const drawn = readSlot(ds, "series")
      .map((key) => ({ key, d: liveTagData[key.toLowerCase()] }))
      .filter((x): x is { key: string; d: NonNullable<typeof x.d> } => Boolean(x.d && x.d.series.length > 0));
    // Each line is named as the machine page names its reading, with its machine where lines in one unit come from
    // more than one (reading-names.ts).
    const names = chartReadingNames(drawn.map((x) => x.d));
    const live: TimeseriesSeries[] = drawn.map(({ key, d }, i) => ({
      id: d.tagId,
      label: names[i],
      unit: d.unit,
      points: d.series,
      ...(d.bucketMs && { bucketMs: d.bucketMs }),
      ...seriesBand(bands?.[key.toLowerCase()]),
    }));
    if (live.length > 0) return live;
  }
  // Live twin scrap-trend: a rejection/scrap key is served by the daily
  // scrap-rate series in agg (the casting lots' or the heats', scrapTrendOf).
  // Live mode only — stub mode falls through to the fixture series below so
  // the backend-less demo is unchanged.
  if (isLiveApi && ds) {
    const keyLower = (readSlot(ds, "series")[0] ?? "").toLowerCase();
    if (keyLower.includes("rejection") || keyLower.includes("scrap")) {
      if (agg.dailyScrapTrend.length > 0) {
        return [
          {
            id: "scrap_pct",
            label: "Daily scrap %",
            unit: "%",
            points: agg.dailyScrapTrend,
          },
        ];
      }
    }
  }
  const out = [] as Array<typeof FX_BATH_TEMP>;
  if (!ds) return [FX_BATH_TEMP];
  for (const key of readSlot(ds, "series")) {
    const k = key.toLowerCase();
    if (k.includes("bath") || k.includes("temp")) out.push(FX_BATH_TEMP);
    else if (k.includes("power")) out.push(FX_POWER_KW);
    else if (k.includes("moisture")) out.push(FX_SAND_MOISTURE);
  }
  return out.length > 0 ? out : [FX_BATH_TEMP];
}
